import React, { createContext, useState, useContext, useCallback } from 'react';
import { authService } from "../services/api";
import { storageService } from "../services/storage";

const UserContext = createContext();

export function UserProvider({ children }) {
    const [roleData, setRoleDataState] = useState(localStorage.getItem('roleData'));
    // User data is fetched from SSO and cached in web storage
    const [user, setUserState] = useState(null);
    // Per-course roles from /auth/me: [{ crn, role }]
    const [courseRoles, setCourseRoles] = useState([]);

    const setRoleData = useCallback((role) => {
        setRoleDataState(role);
        if (role) {
            localStorage.setItem('roleData', role);
        } else {
            localStorage.removeItem('roleData');
        }
    }, []);

    const setUser = useCallback((userObj) => {
        if (!userObj) {
            setUserState(null);
            // Clear cached user name from web storage
            storageService.removeItem('userName');
            return;
        }

        // Extract name from SSO response and normalize
        const ssoName = userObj.name || userObj.displayName || userObj.firstName || userObj.lastName || null;
        
        const normalized = {
            ...userObj,
            loginID: userObj.gtid || userObj.loginID || userObj.username || null,
            name: ssoName,
            role: typeof userObj.role === 'string' ? userObj.role : (Array.isArray(userObj.role) ? userObj.role[0] : null),
            studentID: userObj.studentID ?? userObj.student_id ?? userObj.studentId ?? null,
        };

        console.log("UserContext - Raw user object from SSO:", userObj);
        console.log("UserContext - Normalized user object:", normalized);
        console.log("UserContext - Name cached from SSO:", ssoName);

        setUserState(normalized);

        // Store per-course roles if provided by /auth/me
        if (Array.isArray(userObj.courseRoles)) {
            setCourseRoles(userObj.courseRoles);
        }
        // Cache the user's name from SSO in web storage (not database)
        if (ssoName) {
            storageService.setUserName(ssoName);
        }

        // Set role data (non-sensitive) - can stay in localStorage
        try {
            const rawRole = (userObj.role || '').toString().trim().toLowerCase();
            if (rawRole.includes('instructor') || rawRole === 'ta' || rawRole === 'admin') {
                setRoleData('instructor');
            } else if (rawRole.includes('student')) {
                setRoleData('student');
            } else {
                setRoleData('undecided');
            }
        } catch (e) {
            // ignore parsing errors
        }
    }, [setRoleData]);

    /**
     * Get the user's role for a specific course CRN.
     * Returns "INSTRUCTOR" or "STUDENT". Defaults to "STUDENT" if not found.
     */
    const getCourseRole = useCallback((crn) => {
        if (!crn || !Array.isArray(courseRoles)) return 'STUDENT';
        const entry = courseRoles.find(cr => cr.crn === crn);
        return entry?.role ?? 'STUDENT';
    }, [courseRoles]);

    const logout = useCallback(async () => {
        try {
            // ask backend to revoke tokens / clear session; ignore failure and still clear client state
            await authService.logout();
        } catch (err) {
            console.error("Backend logout failed:", err);
        } finally {
            // Clear all cached data
            const keysToKeep = ['reflection_', 'reflectionCompletedSets_', 'summary_', 'summaryRating_', 'congratsMessage_'];
            Object.keys(localStorage).forEach(key => {
              if (!keysToKeep.some(prefix => key.startsWith(prefix))) {
                localStorage.removeItem(key);
              }
            });
            storageService.clearUserData();

            sessionStorage.clear();
            
            // Clear cookies by setting them to expire
            document.cookie.split(";").forEach((c) => {
                document.cookie = c
                    .replace(/^ +/, "")
                    .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
            });
            
            // Clear any service worker caches if present
            if ('caches' in window) {
                caches.keys().then(names => {
                    names.forEach(name => caches.delete(name));
                });
            }
            setRoleDataState(null);
            setUser(null);
            window.location.href = '/home';
        }
    }, [setUser]);

    // Get cached user name from web storage
    const getCachedUserName = useCallback(() => {
        return storageService.getUserName();
    }, []);

    return (
        <UserContext.Provider value={{ roleData, setRoleData, user, setUser, logout, courseRoles, getCourseRole, getCachedUserName }}>
            {children}
        </UserContext.Provider>
    );
}

export function useUser() {
    return useContext(UserContext);
}
